import { useCallback, useEffect, useState } from 'react';
import { isHostOrCoHost, mapZoomRole, type ClientRole } from '@zcs/shared';
import { isLikelyEmbeddedInZoom, zoomSdk } from './zoomSdk';
import { withTimeout } from './promise-timeout';

const SDK_CALL_TIMEOUT_MS = 10_000;
const RETRY_DELAYS_MS = [2000, 5000];

export interface MeetingParticipant {
  /** Grant userId — matches getUserContext participantUUID / panel WS userId. */
  participantUUID: string;
  screenName: string;
  role: ClientRole;
}

export type MeetingParticipantsState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; participants: MeetingParticipant[] }
  | { status: 'error'; message: string };

function participantIdString(id: string | number | undefined): string {
  if (id == null) return '';
  return String(id).trim();
}

function normalizeParticipant(raw: {
  participantUUID?: string;
  participantId?: string | number;
  screenName?: string;
  role?: string;
}): MeetingParticipant | null {
  const participantUUID =
    raw.participantUUID?.trim() || participantIdString(raw.participantId);
  if (!participantUUID) return null;
  return {
    participantUUID,
    screenName: raw.screenName?.trim() || 'Unknown',
    role: mapZoomRole(raw.role),
  };
}

function extractParticipantList(response: unknown): unknown[] {
  if (!response || typeof response !== 'object') {
    return Array.isArray(response) ? response : [];
  }
  const obj = response as Record<string, unknown>;
  if (Array.isArray(obj.participants)) return obj.participants;
  if (Array.isArray(obj.participantList)) return obj.participantList;
  return [];
}

function mergeParticipantChange(
  current: MeetingParticipant[],
  event: {
    participants?: Array<{
      status?: string;
      participantUUID?: string;
      participantId?: string | number;
      screenName?: string;
      role?: string;
    }>;
  },
): MeetingParticipant[] {
  const byId = new Map(current.map((p) => [p.participantUUID, p]));

  for (const change of event.participants ?? []) {
    const id =
      change.participantUUID?.trim() || participantIdString(change.participantId);
    if (!id) continue;

    if (change.status === 'leave') {
      byId.delete(id);
      continue;
    }

    const normalized = normalizeParticipant(change);
    if (normalized) {
      byId.set(id, normalized);
    }
  }

  return [...byId.values()].sort((a, b) =>
    a.screenName.localeCompare(b.screenName, undefined, { sensitivity: 'base' }),
  );
}

const DEV_MOCK_PARTICIPANTS: MeetingParticipant[] = [
  { participantUUID: 'dev-participant-1', screenName: 'Alex Producer', role: 'participant' },
  { participantUUID: 'dev-participant-2', screenName: 'Jordan TD', role: 'participant' },
  { participantUUID: 'dev-cohost', screenName: 'Sam Co-Host', role: 'co-host' },
];

function devMockRoster(): MeetingParticipantsState {
  return { status: 'ready', participants: DEV_MOCK_PARTICIPANTS };
}

async function fetchMeetingParticipants(): Promise<MeetingParticipant[]> {
  const response = await withTimeout(
    async () => zoomSdk.getMeetingParticipants(),
    SDK_CALL_TIMEOUT_MS,
    'getMeetingParticipants',
  );

  const participants: MeetingParticipant[] = [];
  for (const raw of extractParticipantList(response)) {
    const normalized = normalizeParticipant(
      raw as {
        participantUUID?: string;
        participantId?: string;
        screenName?: string;
        role?: string;
      },
    );
    if (normalized) participants.push(normalized);
  }

  return participants.sort((a, b) =>
    a.screenName.localeCompare(b.screenName, undefined, { sensitivity: 'base' }),
  );
}

export function useMeetingParticipants(enabled: boolean): MeetingParticipantsState {
  const [state, setState] = useState<MeetingParticipantsState>(
    enabled ? { status: 'loading' } : { status: 'idle' },
  );

  const applyParticipants = useCallback((participants: MeetingParticipant[]) => {
    setState({ status: 'ready', participants });
  }, []);

  useEffect(() => {
    if (!enabled) {
      setState({ status: 'idle' });
      return;
    }

    if (import.meta.env.DEV && !isLikelyEmbeddedInZoom()) {
      setState(devMockRoster());
      return;
    }

    let cancelled = false;
    const retryTimers: number[] = [];
    setState({ status: 'loading' });

    const load = async () => {
      try {
        const participants = await fetchMeetingParticipants();
        if (!cancelled) applyParticipants(participants);
        return participants;
      } catch {
        if (!cancelled) {
          setState({
            status: 'error',
            message:
              'Could not load participants. Enable getMeetingParticipants in the Zoom Marketplace.',
          });
        }
        return null;
      }
    };

    void load().then((initial) => {
      if (cancelled || initial === null) return;
      for (const delay of RETRY_DELAYS_MS) {
        retryTimers.push(
          window.setTimeout(() => {
            void load();
          }, delay),
        );
      }
    });

    const onChange = (event: {
      participants?: Array<{
        status?: string;
        participantUUID?: string;
        participantId?: string | number;
        screenName?: string;
        role?: string;
      }>;
    }) => {
      if (cancelled) return;
      setState((prev) => {
        const base = prev.status === 'ready' ? prev.participants : [];
        return {
          status: 'ready',
          participants: mergeParticipantChange(base, event),
        };
      });
    };

    try {
      zoomSdk.onParticipantChange(onChange);
    } catch {
      // Event unsupported in this client — initial list still usable.
    }

    return () => {
      cancelled = true;
      for (const id of retryTimers) window.clearTimeout(id);
    };
  }, [enabled, applyParticipants]);

  return state;
}

export function canGrantParticipant(
  participant: MeetingParticipant,
  selfUserId: string,
): boolean {
  if (participant.participantUUID === selfUserId) return false;
  return !isHostOrCoHost(participant.role);
}

export function displayNameForUserId(
  userId: string,
  participants: MeetingParticipant[],
): string {
  const match = participants.find((p) => p.participantUUID === userId);
  if (match) return match.screenName;
  if (userId.length > 12) return `${userId.slice(0, 8)}…`;
  return userId;
}
